import { useProfileContext } from '../../../contexts/ProfileContext'
import { LoadingView } from '../../components/loading-view/LoadingView'
import { useRepositoriesContext } from '../../../contexts/RepositoriesContext'
import { ErrorView } from '../../components/error-view/ErrorView'
import { Profile } from '../../../core/models/entities/Profile'
import { Organization } from '../../../core/models/entities/Organization'
import { useSearchParams } from 'react-router-dom'
import { BaseLogo } from '../../../core/models/entities/BaseLogo'
import { PageQuery } from '../../../utils/models/pageQuery'
import { useCustomerAndVendors } from '../../shared/hooks/useCustomerAndVendors'
import { Color, LogoOptions } from '../../../core/models/entities/LogoOptions'
import { ComponentState, useComponentStateMachine } from '../../shared/hooks/useComponentStateMachine'
import { useLogoContext } from '../design-editor2/contexts/logo-options.context'
import { LogoEditorComponent } from './componenets/logo-editor.component'
import { useHealPendingHighRes } from '../../../hooks/useHealPendingHighRes'
import { enviroment } from '../../../enviroment'
import { isLogoDetailFromList } from '../../../data/repositories/logoDetailCache'
import { useEffect, useRef } from 'react'

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION - build-time constant.
const healPerMutationEnabled = enviroment.logoHealPerMutationEnabled

export const getAllQuery = { page: { index: 1, size: 0 } } as PageQuery

function LogoEditorScreen() {
  const { profile } = useProfileContext()
  const { state, dataTransition } = useComponentStateMachine()
  const { organizationRepository: organizationRepository } = useRepositoriesContext()
  const { options } = useLogoContext()

  const {
    customers,
    vendors,
    isError: isOrganizationError,
    isSuccess: isOrganizationSuccess,
    isLoading: isOrganizationLoading
  } = useCustomerAndVendors(organizationRepository)

  const isLoading = isOrganizationLoading
  const isError = isOrganizationError
  const isSuccess = isOrganizationSuccess
  dataTransition(isLoading, isSuccess, isError)

  const [sp] = useSearchParams()
  const logoId = Number(sp.get('logo'))
  const orgId = Number(sp.get('organization'))

  return state === ComponentState.Succeed
    ? logoId && orgId
      ? <LogoEditorScreenWithPreselectedLogo orgId={orgId} logoId={logoId} options={options!} profile={profile!} vendors={vendors!} customers={customers!} />
      : <LogoEditorComponent profile={profile!} vendors={vendors!} customers={customers!} options={options!} />

    : state === ComponentState.Loading
      ? <LoadingView />
      : <ErrorView />
}

function LogoEditorScreenWithPreselectedLogo({ logoId, orgId, profile, vendors, customers, options }: {
  logoId: BaseLogo['id'],
  orgId: Organization['id'],
  profile: Profile,
  customers: Organization[];
  vendors: Organization[];
  options: LogoOptions
}) {
  const { logoRepository } = useRepositoriesContext()
  const { data, isLoading, isError, refetch } = logoRepository.useLogo(orgId, logoId)

  // Flag REACT_APP_LOGO_POPUP_FAST: the popup seeds the detail entry with the
  // LIST row (180px image, designCount 0) and replaces it with ONE background
  // GetLogo. "Edit" is live before that lands, so this screen can mount on
  // the stub - react-query reports it as loaded data (isLoading false). The
  // editor freezes preselectedLogo into its own state (selectedLogo /
  // logoForm), so mounting on the stub would put the 180px row on the canvas
  // for the whole session. Treat the stub as "still loading": the stub is
  // stamped updatedAt 0, so the observer above loads on mount regardless of
  // refetchOnMount/staleTime and dedupes with the popup's in-flight call -
  // still exactly one GetLogo. Never true with the flag off (nobody seeds).
  const detailIsStub = isLogoDetailFromList(data)
  const realDetail = detailIsStub ? undefined : data

  // A stub on screen always has a load on the way, whatever its stamp: an
  // entry a writer stamped fresh would not load on mount (refetchOnMount is
  // off globally), and the LoadingView above would then stay for good. Once
  // per mount; react-query dedupes it into a mount-load or the popup's
  // in-flight call, so it never becomes a second GetLogo. A failed load ends
  // in the ErrorView below rather than a retry.
  const backgroundFetchFired = useRef(false)
  useEffect(() => {
    if (!detailIsStub || backgroundFetchFired.current) return
    backgroundFetchFired.current = true
    void refetch()
  }, [detailIsStub, refetch])

  // The editor is the FIRST surface after "create new logo" + upload, and its
  // GetLogo fires ~1s after the upload - while the logo system is still busy
  // with the brand-new serial - so the cached detail carries a blank/identical
  // hi-res and nothing ever refetches it (refetchOnMount is off globally).
  // Bounded background heal (max 3 refetches, stops on success/unmount) so the
  // cache holds the sharp render by the time any reader needs it - no F5.
  // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: the editor view-model runs the
  // heal itself (per mutation, canvas synced) - hand over here so the same
  // logo never gets two budgets (3 + 3 GetLogo).
  // Judged on the real detail only - a stub's identical hi/lo URLs would
  // otherwise start a heal poll for a logo that may already be sharp.
  useHealPendingHighRes(healPerMutationEnabled ? undefined : logoId, realDetail)

  return isLoading || (detailIsStub && !isError)
    ? <LoadingView />
    : detailIsStub
      ? <ErrorView />
      : <LogoEditorComponent preselectedLogo={data!} profile={profile!} vendors={vendors} customers={customers} selectedCustomerId={orgId} options={options!} />
}

export default LogoEditorScreen
